import { useMutation, useQuery } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

import type { IOrganization, IUserOrganization } from "../../interfaces";

export default function useOrganization() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useList() {
        return useQuery({
            queryKey: ["organizations"],
            queryFn: async () => {
                const response = await axiosPrivate.get("/v1/organization/list");
                return parseSuccessResponseData(response);
            },
        });
    }

    function useDescribe(id: string) {
        return useQuery({
            queryKey: ["organizations", id],
            queryFn: async () => {
                if (!id) return null;
                const response = await axiosPrivate.get(`/v1/organization/describe?Id=${id}`);
                return parseSuccessResponseData(response);
            },
            enabled: !!id,
        });
    }

    function useCreate() {
        return useMutation({
            mutationFn: async (data: Partial<IOrganization> & { Name: string }) => {
                const response = await axiosPrivate.post<IOrganization>("/v1/organization/create", data);
                return parseSuccessResponseData(response);
            },
        });
    }

    function useUpdate() {
        return useMutation({
            mutationFn: async (
                data: Partial<IOrganization> & { Id: string; Name: string; IsActive: boolean },
            ) => {
                const response = await axiosPrivate.put<IOrganization>("/v1/organization/update", data);
                return parseSuccessResponseData(response);
            },
        });
    }

    function useListByUser() {
        return useMutation({
            mutationFn: () =>
                axiosPrivate.get(`/v1/organization/list-by-user`).then(parseSuccessResponseData),
        });
    }

    function useAddUser() {
        return useMutation({
            mutationFn: (parameters: { UserId: string; OrganizationId: string; RoleId?: string }) =>
                axiosPrivate.post(`/v1/organization/add-user`, parameters).then(parseSuccessResponseData),
        });
    }

    function useImpersonate() {
        return useMutation({
            mutationFn: (parameters: { OrganizationId: string }) =>
                axiosPrivate
                    .post(`/v1/organization/impersonate`, parameters)
                    .then(parseSuccessResponseData) as Promise<{
                    Message: string;
                    Organization: IUserOrganization;
                }>,
        });
    }

    function useStopImpersonation() {
        return useMutation({
            mutationFn: (parameters: { OrganizationId: string }) =>
                axiosPrivate
                    .post(`/v1/organization/impersonate/stop`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    function useUploadLogo() {
        return useMutation({
            mutationFn: async (parameters: { OrganizationId: string; File: File }) => {
                const formData = new FormData();
                formData.append("OrganizationId", parameters.OrganizationId);
                formData.append("File", parameters.File);
                const response = await axiosPrivate.post("/v1/organization/logo", formData);
                return parseSuccessResponseData(response) as { AssetId: string };
            },
        });
    }

    return {
        useList,
        useDescribe,
        useCreate,
        useUpdate,
        useListByUser,
        useAddUser,
        useImpersonate,
        useStopImpersonation,
        useUploadLogo,
    };
}
